import { useAppSelector } from 'src/store';
import { getAppRoutesAvailability } from 'src/routes/state/routesSelector';
import { useLocation } from 'react-router-dom';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { tableRouteSettings } from 'src/app/tables/Tables';
import { FeatureType } from 'src/shared/types';
import useFeature from 'src/shared/hooks/useFeature';
import useHabitatInsightsAccess from 'src/shared/hooks/useHabitatInsightsAccess';
import { isWithheld } from 'src/shared/utils/productAccess';
import { useIsWithheldFromUser, useIsSetToHidden } from 'src/shared/hooks/useProductLicensing';
import { ProductKey } from 'src/shared/types/engine/products';
import { NestedSidebarMenuSubItem } from './NestedSidebarMenuItem';

const useTablesMenuItem = (): NestedSidebarMenuSubItem | null => {
    const appRoutesAvailability = useAppSelector(getAppRoutesAvailability);
    const navigate = useAppNavigation();
    const { pathname } = useLocation();
    const { hasFeature } = useFeature();
    const isHabitatInsightsProject = hasFeature({ feature: FeatureType.HLCA });
    const isEDnaProject = hasFeature({ feature: FeatureType.EDNA });
    const habitatInsightsAccess = useHabitatInsightsAccess();
    const showsHabitatInsightsMetrics = isHabitatInsightsProject && !isWithheld(habitatInsightsAccess);
    const isEdnaWithheld = useIsWithheldFromUser(ProductKey.EDNA);
    const isHabitatInsightsSetToHidden = useIsSetToHidden(ProductKey.HABITAT_INSIGHTS);
    const isEdnaSetToHidden = useIsSetToHidden(ProductKey.EDNA);

    if (!appRoutesAvailability.tables) {
        return null;
    }

    const subItems = Object.values(tableRouteSettings)
        .map(route => {
            const isHabitatInsightsMetrics = route.name === tableRouteSettings.habitatInsightsMetrics.name;
            const isSampleMetrics = route.name === tableRouteSettings.sampleMetrics.name;

            if (isHabitatInsightsMetrics && showsHabitatInsightsMetrics) {
                return {
                    title: route.name,
                    testId: `${route.name.toLowerCase().replace(/\s+/g, '-')}-menu-item`,
                    to: navigate.paths.habitatInsightsMetricsTable(),
                    isActive: pathname.includes('/tables' + route.path),
                    isHiddenFromCustomers: isHabitatInsightsSetToHidden,
                };
            }

            if (isSampleMetrics && !isEdnaWithheld && (isEDnaProject || !isHabitatInsightsProject)) {
                return {
                    title: route.name,
                    testId: `${route.name.toLowerCase().replace(/\s+/g, '-')}-menu-item`,
                    to: navigate.paths.sampleMetricsTable(),
                    isActive: pathname.includes('/tables' + route.path),
                    isHiddenFromCustomers: isEdnaSetToHidden,
                };
            }
            return null;
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item));

    if (subItems.length === 0) {
        return null;
    }

    return {
        title: 'Tables',
        testId: 'tables-menu-button',
        subItems,
    };
};

export default useTablesMenuItem;
